import { InsurancePolicy, InsuranceQuoteType, QuoteTypeCodes } from '@/interfaces';
import { PolicyStatus } from '@/enums/policy-status.enum';
import { Discriminators } from '@/constants/insurance-policy-const';
import { isEligibleForUpdate } from './InsurancePolicyStatusRules';

export const getActionBarDisplayDetails = async (
  insurancePolicy: InsurancePolicy,
  selfServiceToggles: any,
  renewalInsurancePolicy?: InsurancePolicy
) => {
  const result = {
    showContainer: false,
    showMakeClaim: false,
    showMakeClaimVehicleMultiple: false,
    showMakePayment: false,
    showPaymentDetails: false,
    showChangeVehicleLocation: false,
    showChangeVehicleLocationMultiple: false,
    showUpdatePropertyDetails: false,
    showSwitchToMonthly: false,
  };

  if (!insurancePolicy || !insurancePolicy?.coverages) {
    return result;
  }

  const hasMultipleVehicleCoverages = insurancePolicy.coverages.filter((c: any) => c.isMotor).length > 1;

  const allowableCoverages = [
    QuoteTypeCodes[InsuranceQuoteType.MotorComprehensive],
    QuoteTypeCodes[InsuranceQuoteType.MotorThirdPartyLiability],
    QuoteTypeCodes[InsuranceQuoteType.MotorFireTheftAndThirdPartyLiability],
  ];

  const locationUpdateAllowed =
    insurancePolicy.coverages.filter((c) => allowableCoverages.indexOf(c.coverageCode) > -1)?.length > 0;

  const isDisabled = isOnlineClaimsSwitchedOff(insurancePolicy, selfServiceToggles);

  /* Make a claim*/
  result.showMakeClaimVehicleMultiple = hasMultipleVehicleCoverages;
  result.showMakeClaim = isDisabled === false && !result.showMakeClaimVehicleMultiple;

  /*
   Make a payment
  */
  result.showMakePayment = isPaymentDue(insurancePolicy);

  /* Change payment method*/
  result.showPaymentDetails =
    selfServiceToggles.isChangePaymentDetailsButtonEnabled &&
    insurancePolicy?.currentTermPaymentDetails?.discriminator === Discriminators.NonResponsivePaymentDetails;

  /* Change vehicle location*/
  result.showChangeVehicleLocationMultiple = hasMultipleVehicleCoverages;
  result.showChangeVehicleLocation = locationUpdateAllowed && !hasMultipleVehicleCoverages;

  /* Cyclone flood*/
  const hasHomeCoverage = insurancePolicy.coverages.some((x) => x.isHome);
  const eligibleForUpdateResult = isEligibleForUpdate(insurancePolicy, true);
  //If renewal exists check if it's eligible otherwise ignore it.
  const renewalEligibleForUpdateResult =
    renewalInsurancePolicy !== undefined ? isEligibleForUpdate(renewalInsurancePolicy, false) : undefined;

  const shouldShowCycloneUpdateButton =
    (eligibleForUpdateResult &&
      selfServiceToggles.isHomeCycloneFloodPreferenceEnabled &&
      renewalEligibleForUpdateResult) ||
    renewalEligibleForUpdateResult === undefined;
  result.showUpdatePropertyDetails = shouldShowCycloneUpdateButton && hasHomeCoverage;

  result.showSwitchToMonthly = showSwitchToMonthly(insurancePolicy, selfServiceToggles);

  result.showContainer =
    result.showMakeClaim ||
    result.showMakeClaimVehicleMultiple ||
    result.showMakePayment ||
    result.showPaymentDetails ||
    result.showChangeVehicleLocation ||
    result.showChangeVehicleLocationMultiple;

  return result;
};

export const isPaymentDue = (insurancePolicy: InsurancePolicy) => {
  const responsivePaymentDetails =
    insurancePolicy?.currentTermPaymentDetails?.discriminator === Discriminators.ResponsivePaymentDetails
      ? insurancePolicy.currentTermPaymentDetails
      : null;
  const renewalTermPaymentDetails =
    insurancePolicy?.renewalTermPaymentDetails?.discriminator === Discriminators.ResponsivePaymentDetails
      ? insurancePolicy.renewalTermPaymentDetails
      : null;

  let showMakePayment = false;

  if (
    (responsivePaymentDetails != null &&
      responsivePaymentDetails.totalAmountDue &&
      responsivePaymentDetails.totalAmountDue > 0) ||
    (renewalTermPaymentDetails != null &&
      renewalTermPaymentDetails.totalAmountDue &&
      renewalTermPaymentDetails.totalAmountDue > 0)
  ) {
    showMakePayment = true;
  }

  return showMakePayment;
};

export const isOnlineClaimsSwitchedOff = (insurancePolicy: InsurancePolicy, selfServiceToggles: any): boolean => {
  const coverage = insurancePolicy.coverages[0];

  switch (coverage.coverageCode) {
    case QuoteTypeCodes[InsuranceQuoteType.Contents]:
    case QuoteTypeCodes[InsuranceQuoteType.Home]:
    case QuoteTypeCodes[InsuranceQuoteType.HomeAndContents]:
      return selfServiceToggles.isOnlineClaimsSwitchedOff_Home;
    case QuoteTypeCodes[InsuranceQuoteType.MotorComprehensive]:
    case QuoteTypeCodes[InsuranceQuoteType.MotorFireTheftAndThirdPartyLiability]:
    case QuoteTypeCodes[InsuranceQuoteType.MotorThirdPartyLiability]:
      return selfServiceToggles.isOnlineClaimsSwitchedOff_Motor;
    case QuoteTypeCodes[InsuranceQuoteType.PetComp]:
      return selfServiceToggles.isOnlineClaimsSwitchedOff_Pet;
  }

  return true;
};

export const showSwitchToMonthly = (insurancePolicy: InsurancePolicy, selfServiceToggles: any): boolean => {
  //Ignore canSwitchToMonthlyPayments property as sitecore 8 does anyway, rely on termType annual instead.
  const isValid =
    (insurancePolicy.status === PolicyStatus.ACTIVE &&
      !insurancePolicy.state.isInRenewal &&
      insurancePolicy?.state?.isPaymentDue &&
      insurancePolicy?.jobType?.toLowerCase() === 'submission') ||
    (insurancePolicy.status === PolicyStatus.ACTIVE &&
      insurancePolicy.state.isInRenewal &&
      insurancePolicy?.state?.isPaymentDue);

  return (
    selfServiceToggles.enableSwitchMonthlyPaymentButton &&
    insurancePolicy.currentTermPaymentDetails.discriminator === Discriminators.ResponsivePaymentDetails &&
    insurancePolicy?.termType?.toLowerCase() === 'annual' &&
    isValid
  );
};
